// A PCBA BOM's Board tab (ERP-PLAN §5.15; Mitch, 2026-11-01: "make it clear exactly what components are consigned, and
// which have to be purchased by our CM … crystal clear"): the board's components, read from its KiCad schematics (the
// spec's "<board> KiCad project.zip", never an exported BOM: server/src/board.ts), in three bands: what HHL sends (a BOM
// line with that part, the row's references in its Ref), what the CM buys (no line needed, or a line naming the part it
// buys), and the BOM's lines that aren't on the board (joysticks on wires, the bag). On a draft, "Choose part…" ties a row
// to a part (or a new part made from it: one per reference, so its qty is its references), saying who supplies it (HHL
// sends it, or the CM buys that part), and "Untie" takes the row's references off its line (the line stays, under "not
// on the board", until it's removed). Who supplies a tied part is sourcing, not part of a revision: it switches on the
// revision in use too (bom.set_line_supply).
// "Download for <CM>" is the board's BOM with who supplies each row (boardBomCsv()).
import { AlertTriangle, Cpu, Download, Plus, Trash2, Unlink } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { type BoardBom, boardBomCsv, boardMatch, refsOf, withRefs } from "../../../shared/board-bom.ts";
import { type BomIndex, type BomLineRow, type BomRow, type Usage, whereUsed } from "../../../shared/boms.ts";
import type { FileVersion, Item } from "../../../shared/db.gen.ts";
import { skuStem } from "../../../shared/part-sku.ts";
import { categoryOptions, categoryPatch, typePatch } from "../../../shared/part-type.ts";
import { qty as fmtQty } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { Link } from "../lib/router.tsx";
import { useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button, cx, EmptyState, Select } from "../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../ui/DataTable.tsx";
import { Popover } from "../ui/Popover.tsx";
import { toast } from "../ui/toast.tsx";
import { PartPicker } from "./BomPicker.tsx";
import { itemPath } from "./ItemsPage.tsx";
import { useSpecIndex } from "./specs/lib.ts";

export type Board = BoardBom & { name: string };

const KICAD_ZIP = /KiCad project\.zip$/i;

/** The KiCad project the item's spec keeps (the newest upload, when it has more than one), or null. */
export function useBoardProject(itemId: string | null | undefined): FileVersion | null {
  const sx = useSpecIndex();
  return useMemo(() => {
    const spec = itemId ? sx.specByItem.get(itemId) : undefined;
    const versions = (spec ? (sx.filesBySpec.get(spec.id) ?? []) : [])
      .map((f) => sx.versionById.get(f.current_version_id ?? ""))
      .filter((v): v is FileVersion => !!v && KICAD_ZIP.test(v.filename));
    return versions.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))[0] ?? null;
  }, [sx, itemId]);
}

// A version never changes, so each is read once per page load.
const boards = new Map<string, Promise<Board>>();
function loadBoard(versionId: string) {
  let p = boards.get(versionId);
  if (!p) {
    p = fetch(`/api/file-versions/${versionId}/board`).then(async (res) => {
      const body = (await res.json().catch(() => ({}))) as Board & { error?: string };
      if (!res.ok) throw new Error(body.error ?? `The board couldn't be read (${res.status})`);
      return body;
    });
    p.catch(() => boards.delete(versionId));
    boards.set(versionId, p);
  }
  return p;
}

/** The board in this KiCad project version, once read. */
export function useBoard(versionId: string | null | undefined): { board?: Board; error?: string } | null {
  const [state, setState] = useState<{ id: string; board?: Board; error?: string } | null>(null);
  useEffect(() => {
    if (!versionId) return;
    let live = true;
    loadBoard(versionId).then(
      (board) => live && setState({ id: versionId, board }),
      (e: Error) => live && setState({ id: versionId, error: e.message }),
    );
    return () => {
      live = false;
    };
  }, [versionId]);
  return versionId && state?.id === versionId ? state : null;
}

/** Who builds the board: its own maker, else the maker of what uses it (it's built in the same order). */
function makersOf(ix: BomIndex, bom: BomRow): string[] {
  if (bom.make_vendor_id) return [bom.make_vendor_id];
  const ids = new Set<string>();
  const walk = (usages: Usage[]) => {
    for (const u of usages) {
      if (u.bom.make_vendor_id) ids.add(u.bom.make_vendor_id);
      else walk(u.parents);
    }
  };
  walk(whereUsed(ix, bom.item_id ?? ""));
  return [...ids];
}

/** A board's component count (each reference once). */
export const partsOn = (board: Pick<BoardBom, "rows">) => board.rows.reduce((n, r) => n + r.qty, 0);

type Band = "hhl" | "cm" | "bom";
type Row = {
  id: string;
  order: number;
  band: Band;
  refs: string[];
  qty: number;
  value: string;
  footprint: string;
  mpn: string;
  manufacturer: string;
  offBoard: boolean;
  line: BomLineRow | null;
  /** The row's references its line doesn't have; a line's references not on the board. */
  untied: string[];
  gone: string[];
};

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function BoardTab({ ix, bom, editable, project }: { ix: BomIndex; bom: BomRow; editable: boolean; project: FileVersion | null }) {
  const state = useBoard(project?.id);
  const vendors = useRows("vendor");
  const item = ix.itemById.get(bom.item_id ?? "");
  const makers = useMemo(() => makersOf(ix, bom), [ix, bom]);
  const cm = (makers.length === 1 && vendors.find((v) => v.id === makers[0])?.name) || "The CM";
  const lines = ix.linesByBom.get(bom.id) ?? [];
  const sourcing = bom.status !== "retired" && can("bom.set_line_supply");
  const [making, setMaking] = useState<{ row: Row; supply: TieSupply } | null>(null);
  const board = state?.board;

  const rows = useMemo<Row[]>(() => {
    if (!board) return [];
    const m = boardMatch(board.rows, lines);
    const onBoard = board.rows.map((r, i): Row => {
      const line = m.lineOf[i] as BomLineRow | null;
      return {
        ...r,
        id: `ref-${r.refs[0]}`,
        order: i,
        band: line?.supply === "hhl_consigns" ? "hhl" : "cm",
        line,
        untied: m.untied[i],
        gone: line ? (m.gone.get(line.id) ?? []) : [],
      };
    });
    const off = (m.offBoard as BomLineRow[]).map((l, i): Row => {
      const part = ix.itemById.get(l.component_item_id ?? "");
      return {
        id: `line-${l.id}`,
        order: board.rows.length + i,
        band: "bom",
        refs: refsOf(l.ref_designator ?? ""),
        qty: Number(l.qty),
        value: part?.name ?? l.option_name,
        footprint: "",
        mpn: "",
        manufacturer: "",
        offBoard: false,
        line: l,
        untied: [],
        gone: refsOf(l.ref_designator ?? ""),
      };
    });
    return [...onBoard, ...off];
  }, [board, lines, ix]);

  if (!project)
    return (
      <EmptyState icon={<Cpu className="size-6" />} title="No KiCad project for this board yet">
        A board's components come from its KiCad schematics. Drop the board's KiCad project folder on{" "}
        {item ? (
          <Link to={itemPath(item)} className="text-accent-text hover:underline">
            {item.name}
          </Link>
        ) : (
          "the part"
        )}
        's spec (Files), and they show here.
      </EmptyState>
    );
  if (!state) return <div className="p-6 text-muted">Reading the board's schematics…</div>;
  if (state.error || !board)
    return (
      <EmptyState icon={<AlertTriangle className="size-6" />} title="The board couldn't be read">
        {state.error}
      </EmptyState>
    );

  const hhlRows = rows.filter((r) => r.band === "hhl");
  const cmRows = rows.filter((r) => r.band === "cm");
  const sum = (rs: Row[]) => rs.reduce((n, r) => n + r.qty, 0);
  const total = partsOn(board);
  const parts = (n: number) => `${fmtQty(n)} ${n === 1 ? "part" : "parts"}`;

  /** Ties a row to a part: its line on this BOM gets the row's references (one each), or a new line, supplied as said. */
  const tie = async (r: Row, part: Pick<Item, "id" | "sku">, supply: TieSupply) => {
    const existing = lines.find((l) => l.component_item_id === part.id);
    if (existing) {
      const ref = withRefs(existing.ref_designator ?? "", r.refs);
      const n = refsOf(ref).length;
      const ok = await mutate("bom_line.update", { id: existing.id, patch: { ref_designator: ref, qty: n, supply } });
      if (ok && n !== Number(existing.qty)) toast.info(`${part.sku} is on the BOM already: now ${ref}, qty ${existing.qty} → ${n} (one per reference)`);
      return ok;
    }
    const position = Math.max(0, ...lines.map((l) => l.position)) + 1;
    return mutate("bom_line.create", {
      id: crypto.randomUUID(),
      bom_id: bom.id,
      component_item_id: part.id,
      position,
      qty: r.qty,
      supply,
      ref_designator: r.refs.join(", "),
    });
  };
  const untie = (r: Row) => {
    if (!r.line) return;
    const rest = withRefs(r.line.ref_designator ?? "", [], r.refs);
    return mutate("bom_line.update", { id: r.line.id, patch: { ref_designator: rest, ...(rest ? { qty: refsOf(rest).length } : {}) } });
  };

  const section = (r: Row): TableSection =>
    r.band === "hhl"
      ? { key: "hhl", order: 0, label: <BandLabel tone="accent" title="Hand Held Legend sends" note={`${parts(sum(hhlRows))}, sent to ${cm}`} /> }
      : r.band === "cm"
        ? { key: "cm", order: 1, label: <BandLabel title={`${cm} buys`} note={parts(sum(cmRows))} /> }
        : { key: "bom", order: 2, label: <BandLabel title="On the BOM, not on the board" note="Lines with no reference on the schematic" /> };

  const columns: Column<Row>[] = [
    {
      key: "refs",
      header: "References",
      width: "minmax(140px,1.3fr)",
      value: (r) => r.order,
      text: (r) => r.refs.join(", "),
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-mono text-[12px]" title={r.refs.join(", ")}>
            {r.refs.join(", ") || <span className="text-faint">—</span>}
          </span>
          {r.untied.length > 0 && <Badge tone="warn">{r.untied.join(", ")} not on its line</Badge>}
          {r.band !== "bom" && r.gone.length > 0 && <Badge tone="warn">{r.gone.join(", ")} not on the board</Badge>}
        </span>
      ),
    },
    { key: "qty", header: "Qty", width: "56px", align: "right", value: (r) => r.qty, cell: (r) => fmtQty(r.qty) },
    {
      key: "value",
      header: "Value",
      width: "minmax(120px,1fr)",
      cell: (r) => (
        <span className="truncate" title={r.value}>
          {r.value}
          {r.offBoard && <span className="ml-1.5 text-[11px] text-faint">not soldered on</span>}
        </span>
      ),
    },
    {
      key: "footprint",
      header: "Footprint",
      width: "minmax(110px,1fr)",
      cell: (r) => (
        <span className="truncate text-muted" title={r.footprint}>
          {r.footprint}
        </span>
      ),
    },
    {
      key: "mpn",
      header: "Part number",
      width: "minmax(110px,1fr)",
      value: (r) => r.mpn,
      cell: (r) => (
        <span className="truncate" title={[r.manufacturer, r.mpn].filter(Boolean).join(" ")}>
          {r.mpn}
          {r.manufacturer && <span className="ml-1.5 text-muted">{r.manufacturer}</span>}
        </span>
      ),
    },
    {
      key: "part",
      header: "Our part",
      width: "minmax(170px,1.4fr)",
      value: (r) => (r.line ? (ix.itemById.get(r.line.component_item_id ?? "")?.sku ?? "") : ""),
      cell: (r) => {
        const part = r.line ? ix.itemById.get(r.line.component_item_id ?? "") : undefined;
        if (!r.line) return <span className="text-faint">Any that matches</span>;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            {part ? (
              <Link to={itemPath(part)} className="truncate hover:underline" title={part.name}>
                <span className="font-mono text-[12px]">{part.sku}</span> <span className="text-muted">{part.name}</span>
              </Link>
            ) : (
              <span className="text-muted">{r.line.option_name || "No part"}</span>
            )}
            {r.band === "bom" && <Badge>{r.line.supply === "hhl_consigns" ? "HHL sends" : r.line.supply === "cm_makes" ? `${cm} makes` : `${cm} buys`}</Badge>}
          </span>
        );
      },
    },
    ...(editable || sourcing
      ? [
          {
            key: "act",
            header: "",
            width: "190px",
            value: () => null,
            cell: (r: Row) =>
              r.band === "bom" ? (
                editable &&
                r.line && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="size-3.5" />}
                    title="Remove this line from the BOM"
                    onClick={() => r.line && mutate("bom_line.remove", { id: r.line.id })}
                  >
                    Remove line
                  </Button>
                )
              ) : r.line ? (
                <span className="flex items-center gap-1">
                  <Select
                    value={r.line.supply === "hhl_consigns" ? "hhl_consigns" : "cm_buys"}
                    disabled={!sourcing}
                    onChange={(e) => r.line && mutate("bom.set_line_supply", { line_id: r.line.id, supply: e.target.value as "hhl_consigns" | "cm_buys" })}
                    options={[
                      { value: "hhl_consigns", label: "HHL sends" },
                      { value: "cm_buys", label: `${cm} buys` },
                    ]}
                    className="h-7 w-[118px] text-[12px]"
                  />
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Unlink className="size-3.5" />}
                      title={`Not this part: take ${r.refs.join(", ")} off its line`}
                      onClick={() => untie(r)}
                    />
                  )}
                </span>
              ) : editable ? (
                <Popover
                  width={460}
                  button={(toggle) => (
                    <Button
                      size="sm"
                      icon={<Plus className="size-3.5" />}
                      onClick={toggle}
                      title="Say which part goes on these references, and who supplies it"
                    >
                      Choose part…
                    </Button>
                  )}
                >
                  {(close) => (
                    <TiePanel
                      row={r}
                      cm={cm}
                      picker={(supply) => (
                        <PartPicker
                          bom={bom}
                          ix={ix}
                          autoFocus
                          inline
                          placeholder="Find a part… (SKU or name)"
                          onPick={async (part) => {
                            close();
                            await tie(r, part, supply);
                          }}
                          onCancel={close}
                        />
                      )}
                      onNew={(supply) => {
                        close();
                        setMaking({ row: r, supply });
                      }}
                    />
                  )}
                </Popover>
              ) : null,
          },
        ]
      : []),
  ];

  const download = () => {
    const csv = boardBomCsv(board, lines, { cm, skuOf: (l) => ix.itemById.get(l.component_item_id ?? "")?.sku ?? "" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${board.name} BOM, supplied by.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const code = skuStem(item?.sku ?? "").split("-")[0];
  const prefill = (r: Row) => ({
    name: [r.value, r.mpn && r.mpn !== r.value ? r.mpn : ""].filter(Boolean).join(" "),
    sku: [
      code,
      (r.mpn || r.value)
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
    ]
      .filter(Boolean)
      .join("-"),
    category: "electronic",
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-2 border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-1.5 font-medium">
            <Cpu className="size-4 text-faint" />
            {board.name}: {parts(total)} on the board
          </span>
          <Badge tone="accent">HHL sends {parts(sum(hhlRows))}</Badge>
          <Badge>
            {cm} buys {parts(sum(cmRows))}
          </Badge>
          <span className="text-[12px] text-faint" title={project.filename}>
            From the schematics in {project.filename}, uploaded {day(project.uploaded_at)} · {board.sheets} {board.sheets === 1 ? "sheet" : "sheets"}
          </span>
          <Button
            size="sm"
            className="ml-auto"
            icon={<Download className="size-3.5" />}
            onClick={download}
            title={`The board's BOM with who supplies each row, for ${cm}`}
          >
            Download for {cm}
          </Button>
        </div>
        {(board.dnp.length > 0 || board.notInBom.length > 0) && (
          <div className="text-[12px] text-muted">
            {board.dnp.length > 0 && <span>Not fitted (DNP): {board.dnp.join(", ")}</span>}
            {board.dnp.length > 0 && board.notInBom.length > 0 && " · "}
            {board.notInBom.length > 0 && <span>Left out of the BOM in KiCad: {board.notInBom.join(", ")}</span>}
          </div>
        )}
        {board.warnings.map((w) => (
          <div key={w} className="flex items-center gap-1.5 text-[12px] text-warn">
            <AlertTriangle className="size-3.5 shrink-0" />
            {w}
          </div>
        ))}
        {!editable && hhlRows.length + cmRows.length > 0 && bom.status !== "draft" && (
          <div className="text-[12px] text-faint">Who supplies a tied part changes here; tying a component to a part takes a new revision.</div>
        )}
      </div>
      <div className="min-h-0 flex-1">
        <DataTable
          rows={rows}
          columns={columns}
          sectionOf={section}
          defaultSort={{ key: "refs" }}
          rowNoun={["row", "rows"]}
          exportName="Board components"
          pageFilters={false}
          empty={<div className="p-6 text-center text-muted">The schematics have no parts.</div>}
        />
      </div>
      {making && (
        <CreateDialog
          title="New part from the board"
          initial={prefill(making.row)}
          fields={[
            { key: "name", label: "Name" },
            { key: "sku", label: "SKU", prefix: "MAT-" },
            { key: "category", label: "Category", type: "select", options: categoryOptions("material") },
          ]}
          onClose={() => setMaking(null)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const typed = typePatch({ kind: "material" }, "material");
            const ok = await mutate("item.create", { id, name: v.name, sku: v.sku, procurement: "buy", ...typed, ...categoryPatch(typed, v.category) });
            return ok && (await tie(making.row, { id, sku: v.sku }, making.supply));
          }}
        />
      )}
    </div>
  );
}

type TieSupply = "hhl_consigns" | "cm_buys";

/** Choose part's pop-up: who supplies the row (HHL sends it, the default, or the CM buys that part), then the part. */
function TiePanel({ row, cm, picker, onNew }: { row: Row; cm: string; picker: (supply: TieSupply) => ReactNode; onNew: (supply: TieSupply) => void }) {
  const [supply, setSupply] = useState<TieSupply>("hhl_consigns");
  const choices: [TieSupply, string][] = [
    ["hhl_consigns", "HHL sends it"],
    ["cm_buys", `${cm} buys it`],
  ];
  return (
    <div className="flex flex-col gap-2 p-2">
      <div className="px-1 text-[12px] text-muted">
        The part for <span className="font-mono">{row.refs.join(", ")}</span>
        {row.value && ` (${row.value})`}
      </div>
      <div className="flex rounded-md border border-line p-0.5 text-[12px]">
        {choices.map(([value, label]) => (
          <button
            key={value}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setSupply(value)}
            className={cx("flex-1 rounded px-2 py-1", supply === value ? "bg-accent-soft font-medium text-accent-text" : "text-muted hover:text-text")}
          >
            {label}
          </button>
        ))}
      </div>
      {picker(supply)}
      <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => onNew(supply)}>
        New part from this
      </Button>
    </div>
  );
}

function BandLabel({ title, note, tone }: { title: string; note: string; tone?: "accent" }) {
  return (
    <span className="flex items-center gap-2">
      <span className={tone === "accent" ? "text-accent-text" : undefined}>{title}</span>
      <span className="font-normal text-faint normal-case">{note}</span>
    </span>
  );
}

/**
 * Board components --- how the board's components split between HHL and the maker for a revision's lines (a row on an
 * "HHL sends" line is HHL's; on another line, or on none, the maker's), for the Release step to say before releasing.
 */
export function useBoardSplit(ix: BomIndex, bom: BomRow): { total: number; hhl: number; maker: number } | null {
  const project = useBoardProject(bom.item_id);
  const board = useBoard(project?.id)?.board;
  return useMemo(() => {
    if (!board) return null;
    const lines = ix.linesByBom.get(bom.id) ?? [];
    const m = boardMatch(board.rows, lines);
    let hhl = 0;
    let maker = 0;
    board.rows.forEach((r, i) => {
      if ((m.lineOf[i] as BomLineRow | null)?.supply === "hhl_consigns") hhl += r.qty;
      else maker += r.qty;
    });
    return { total: hhl + maker, hhl, maker };
  }, [board, ix, bom.id]);
}
